import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Download, Search, X, Pause, Ban, KeyRound, RotateCcw, AlertTriangle, Mail, Phone, MapPin, Calendar, Pencil, Check } from 'lucide-react';
import {
  adminListUsers,
  adminGetUser,
  adminPauseUser,
  adminSuspendUser,
  adminReactivateUser,
  adminSendResetToUser,
  adminUsersCsvUrl,
  adminSetSpendLimit,
} from '@/lib/api';

const STATUS_BADGE = {
  active: 'bg-[color:var(--pd-accent-yellow)]/15 text-[color:var(--pd-accent-yellow)] border-[color:var(--pd-accent-yellow)]/40',
  paused: 'bg-[color:var(--pd-gold)]/15 text-[color:var(--pd-gold)] border-[color:var(--pd-gold)]/40',
  suspended: 'bg-[color:var(--pd-danger)]/15 text-[color:var(--pd-danger)] border-[color:var(--pd-danger)]/40',
  closed: 'bg-muted-sage/15 text-muted-sage border-muted-sage/30',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return iso; }
};
const formatDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
};
const calcAge = (dobStr) => {
  if (!dobStr) return null;
  try {
    const dob = new Date(dobStr);
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const m = now.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;
    return age;
  } catch { return null; }
};
const formatMoneyPence = (p) => {
  if (p == null) return '£0.00';
  return `£${(Number(p) / 100).toFixed(2)}`;
};

const AdminCustomers = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [flagFilter, setFlagFilter] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ users: [], total: 0, page: 1, returned: 0 });
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null); // full user detail for side panel
  const [detailLoading, setDetailLoading] = useState(false);
  // Inline spend-limit editor state (per-detail-panel, resets when detail changes)
  const [limitEditor, setLimitEditor] = useState({ open: false, value: '', reason: '', saving: false });
  const [actionBusy, setActionBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      if (flagFilter) params.flag = flagFilter;
      const r = await adminListUsers(params);
      setData(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load customers.');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, flagFilter]);

  useEffect(() => { load(); }, [load]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openDetail = async (userId) => {
    setDetailLoading(true);
    setDetail({ id: userId, loading: true });
    try {
      const r = await adminGetUser(userId);
      setDetail(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load customer detail.');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => { setDetail(null); setLimitEditor({ open: false, value: '', reason: '', saving: false }); };

  const openLimitEditor = () => {
    const current = detail?.spend_limit_gbp;
    setLimitEditor({
      open: true,
      value: current == null ? '' : String(current),
      reason: '',
      saving: false,
    });
  };

  const saveSpendLimit = async () => {
    if (!detail?.id) return;
    const raw = limitEditor.value.trim();
    const asNum = raw === '' ? null : Number(raw);
    if (raw !== '' && (Number.isNaN(asNum) || asNum < 0 || asNum > 250)) {
      toast.error('Spend limit must be a whole number between \u00a30 and \u00a3250.');
      return;
    }
    setLimitEditor((s) => ({ ...s, saving: true }));
    try {
      const r = await adminSetSpendLimit(detail.id, asNum, limitEditor.reason.trim() || null);
      toast.success(r.message || 'Spend limit updated.');
      const d = await adminGetUser(detail.id);
      setDetail(d);
      setLimitEditor({ open: false, value: '', reason: '', saving: false });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update spend limit.');
      setLimitEditor((s) => ({ ...s, saving: false }));
    }
  };

  const doAction = async (fn, successMsg) => {
    if (!detail?.id) return;
    setActionBusy(true);
    try {
      await fn(detail.id);
      toast.success(successMsg);
      // refresh detail + list
      const [d] = await Promise.all([adminGetUser(detail.id), load()]);
      setDetail(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed.');
    } finally {
      setActionBusy(false);
    }
  };

  const csvHref = useMemo(() => adminUsersCsvUrl(search.trim()), [search]);

  return (
    <div data-testid="admin-customers-page">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-widest">Admin</div>
          <h1 className="font-display text-4xl mt-1">Customers</h1>
          <p className="text-sm text-muted-sage mt-1">All accounts, searchable. Click any row for full detail and actions.</p>
        </div>
        <a
          href={csvHref}
          download
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-pd bg-surface text-sm hover:border-[color:var(--pd-accent-yellow)]"
          data-testid="admin-customers-export"
        >
          <Download size={15} /> Download CSV
        </a>
      </div>

      {/* Filters */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-sage" />
          <input
            type="text"
            placeholder="Search email, name or phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-11 rounded-lg bg-surface border border-pd pl-9 pr-3 text-sm focus:border-[color:var(--pd-accent-yellow)] focus:outline-none"
            data-testid="admin-customers-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="h-11 rounded-lg bg-surface border border-pd px-3 text-sm focus:border-[color:var(--pd-accent-yellow)] focus:outline-none"
          data-testid="admin-customers-status-filter"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="suspended">Suspended</option>
          <option value="closed">Closed</option>
        </select>
        <select
          value={flagFilter}
          onChange={(e) => { setFlagFilter(e.target.value); setPage(1); }}
          className="h-11 rounded-lg bg-surface border border-pd px-3 text-sm focus:border-[color:var(--pd-accent-yellow)] focus:outline-none"
          data-testid="admin-customers-flag-filter"
        >
          <option value="">All accounts</option>
          <option value="duplicates">Possible duplicates</option>
          <option value="no_limit">No personal limit</option>
          <option value="marketing">Marketing opted-in</option>
        </select>
      </div>

      {/* Table */}
      <div className="mt-4 rounded-2xl border border-pd bg-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-customers-table">
            <thead className="bg-[color:var(--pd-bg)] text-muted-sage font-mono text-[10px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-3">Name / email</th>
                <th className="text-left px-4 py-3">Phone</th>
                <th className="text-left px-4 py-3">Location</th>
                <th className="text-left px-4 py-3">Age</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Flags</th>
                <th className="text-left px-4 py-3">Joined</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-6 text-muted-sage">Loading…</td></tr>
              ) : data.users.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-6 text-muted-sage" data-testid="admin-customers-empty">No customers match this search.</td></tr>
              ) : data.users.map((u) => {
                const age = calcAge(u.date_of_birth);
                return (
                  <tr
                    key={u.id}
                    onClick={() => openDetail(u.id)}
                    className="border-t border-pd hover:bg-[color:var(--pd-accent-yellow)]/5 cursor-pointer"
                    data-testid={`admin-customer-row-${u.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="text-[color:var(--pd-text)]">{u.full_name || '—'}</div>
                      <div className="text-xs text-muted-sage font-mono">{u.email}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{u.phone || '—'}</td>
                    <td className="px-4 py-3">{u.city || '—'}<span className="text-muted-sage">{u.postcode ? ` · ${u.postcode}` : ''}</span></td>
                    <td className="px-4 py-3 font-mono text-xs">{age != null ? age : '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-mono uppercase tracking-widest ${STATUS_BADGE[u.status] || STATUS_BADGE.active}`}>
                        {u.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {u.flagged_for_review ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-mono uppercase tracking-widest bg-[color:var(--pd-danger)]/15 text-[color:var(--pd-danger)] border-[color:var(--pd-danger)]/40">dup</span>
                        ) : null}
                        {u.marketing_opt_in ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-mono uppercase tracking-widest bg-[color:var(--pd-gold)]/15 text-[color:var(--pd-gold)] border-[color:var(--pd-gold)]/40">mkt</span>
                        ) : null}
                        {u.no_spend_limit_acknowledged ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-mono uppercase tracking-widest bg-muted-sage/15 text-muted-sage border-muted-sage/30">no-cap</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-sage">{formatDate(u.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between p-3 border-t border-pd text-xs text-muted-sage font-mono">
          <div>
            {data.total} customer{data.total === 1 ? '' : 's'} total · showing {data.returned}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="px-2 py-1 rounded border border-pd hover:border-[color:var(--pd-accent-yellow)] disabled:opacity-40"
              data-testid="admin-customers-prev"
            >Prev</button>
            <div>Page {page}</div>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={data.returned < 50 || loading}
              className="px-2 py-1 rounded border border-pd hover:border-[color:var(--pd-accent-yellow)] disabled:opacity-40"
              data-testid="admin-customers-next"
            >Next</button>
          </div>
        </div>
      </div>

      {/* Side panel */}
      {detail ? (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={closeDetail}
            data-testid="admin-customer-panel-backdrop"
            aria-hidden
          />
          <div
            className="fixed top-0 right-0 bottom-0 w-full md:w-[520px] bg-surface border-l border-pd z-50 overflow-y-auto"
            data-testid="admin-customer-detail-panel"
          >
            <div className="sticky top-0 bg-surface border-b border-pd px-5 py-4 flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-widest">Customer</div>
                <div className="font-display text-2xl leading-tight" data-testid="admin-customer-detail-name">
                  {detail.full_name || detail.email || 'Loading…'}
                </div>
              </div>
              <button
                onClick={closeDetail}
                className="p-2 rounded-lg hover:bg-[color:var(--pd-bg)]"
                aria-label="Close"
                data-testid="admin-customer-panel-close"
              >
                <X size={18} />
              </button>
            </div>

            {detailLoading || detail.loading ? (
              <div className="p-6 text-sm text-muted-sage">Loading customer detail…</div>
            ) : (
              <div className="p-5 space-y-6">
                {/* Status + flags */}
                <div className="flex flex-wrap gap-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-[10px] font-mono uppercase tracking-widest ${STATUS_BADGE[detail.status] || STATUS_BADGE.active}`} data-testid="admin-customer-detail-status">
                    {detail.status}
                  </span>
                  {detail.flagged_for_review ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded border text-[10px] font-mono uppercase tracking-widest bg-[color:var(--pd-danger)]/15 text-[color:var(--pd-danger)] border-[color:var(--pd-danger)]/40">
                      <AlertTriangle size={11} /> Possible duplicate ({(detail.duplicate_flags || []).join(', ') || 'flagged'})
                    </span>
                  ) : null}
                </div>

                {/* Contact block */}
                <div className="grid grid-cols-1 gap-3 text-sm">
                  <div className="flex items-start gap-2">
                    <Mail size={14} className="text-muted-sage mt-1" />
                    <div><span className="text-muted-sage">Email </span><span className="font-mono" data-testid="admin-customer-detail-email">{detail.email}</span></div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Phone size={14} className="text-muted-sage mt-1" />
                    <div><span className="text-muted-sage">Phone </span><span className="font-mono">{detail.phone || '—'}</span></div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Calendar size={14} className="text-muted-sage mt-1" />
                    <div>
                      <span className="text-muted-sage">DOB </span>
                      <span className="font-mono">{detail.date_of_birth || '—'}</span>
                      {calcAge(detail.date_of_birth) != null ? <span className="text-muted-sage"> ({calcAge(detail.date_of_birth)} years)</span> : null}
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <MapPin size={14} className="text-muted-sage mt-1" />
                    <div className="text-sm">
                      <div>{detail.address_line1 || '—'}</div>
                      {detail.address_line2 ? <div>{detail.address_line2}</div> : null}
                      <div>{[detail.city, detail.postcode].filter(Boolean).join(', ')}</div>
                    </div>
                  </div>
                </div>

                {/* Account bits */}
                <div className="rounded-xl border border-pd p-4 text-sm space-y-1.5">
                  <div className="flex justify-between"><span className="text-muted-sage">Joined</span><span className="font-mono">{formatDateTime(detail.created_at)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-sage">Marketing opt-in</span><span className="font-mono">{detail.marketing_opt_in ? 'yes' : 'no'}</span></div>

                  {/* Spend limit \u2014 inline editable */}
                  <div className="pt-1">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-sage">Personal spend limit</span>
                      {!limitEditor.open && (
                        <button
                          onClick={openLimitEditor}
                          className="inline-flex items-center gap-1 text-[11px] text-[color:var(--pd-accent-yellow)] hover:underline"
                          data-testid="admin-edit-spend-limit"
                        >
                          <Pencil size={11} /> Edit
                        </button>
                      )}
                    </div>
                    {!limitEditor.open ? (
                      <div className="mt-0.5 flex items-center justify-between">
                        <span className="font-mono">
                          {detail.spend_limit_gbp != null
                            ? `\u00a3${detail.spend_limit_gbp}`
                            : (detail.no_spend_limit_acknowledged ? '\u00a3250 (platform cap)' : '\u2014')}
                        </span>
                        {detail.spend_limit_pending_gbp != null ? (
                          <span className="text-[10px] font-mono text-[color:var(--pd-gold)]">
                            queued: &pound;{detail.spend_limit_pending_gbp}
                          </span>
                        ) : null}
                      </div>
                    ) : (
                      <div className="mt-2 rounded-lg border border-[color:var(--pd-accent-yellow)]/40 bg-[color:var(--pd-bg)] p-3 space-y-2" data-testid="admin-spend-limit-editor">
                        <div className="text-[11px] text-muted-sage">
                          Takes effect <span className="text-[color:var(--pd-text)]">immediately</span>. Any queued change is cleared. Max &pound;250.
                        </div>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="block text-[10px] font-mono text-muted-sage uppercase mb-1">New limit (&pound;)</label>
                            <input
                              type="number"
                              min={0}
                              max={250}
                              step={1}
                              value={limitEditor.value}
                              onChange={(e) => setLimitEditor((s) => ({ ...s, value: e.target.value }))}
                              placeholder="e.g. 10"
                              className="w-full bg-surface border border-pd rounded-md px-2 py-1.5 text-sm font-mono focus:outline-none focus:border-[color:var(--pd-accent-yellow)]"
                              data-testid="admin-spend-limit-input"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-[10px] font-mono text-muted-sage uppercase mb-1">Reason (optional)</label>
                            <input
                              type="text"
                              value={limitEditor.reason}
                              onChange={(e) => setLimitEditor((s) => ({ ...s, reason: e.target.value }))}
                              placeholder="e.g. queued increase stuck"
                              className="w-full bg-surface border border-pd rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-[color:var(--pd-accent-yellow)]"
                              data-testid="admin-spend-limit-reason"
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-end gap-2 pt-1">
                          <button
                            onClick={() => setLimitEditor({ open: false, value: '', reason: '', saving: false })}
                            className="text-xs text-muted-sage hover:text-[color:var(--pd-text)] px-2 py-1"
                            data-testid="admin-spend-limit-cancel"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={saveSpendLimit}
                            disabled={limitEditor.saving}
                            className="inline-flex items-center gap-1 bg-[color:var(--pd-accent-yellow)] text-[#14251c] rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-60"
                            data-testid="admin-spend-limit-save"
                          >
                            <Check size={12} /> {limitEditor.saving ? 'Saving\u2026' : 'Save'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between"><span className="text-muted-sage">Age verified</span><span className="font-mono">{detail.age_verified ? `yes (${detail.age_verification_method || 'self-declared'})` : 'no'}</span></div>
                  {detail.pause_until ? <div className="flex justify-between"><span className="text-muted-sage">Paused until</span><span className="font-mono">{formatDateTime(detail.pause_until)}</span></div> : null}
                  {detail.suspended_until ? <div className="flex justify-between"><span className="text-muted-sage">Suspended until</span><span className="font-mono">{formatDateTime(detail.suspended_until)}</span></div> : null}
                  {detail.closed_at ? <div className="flex justify-between"><span className="text-muted-sage">Closed at</span><span className="font-mono">{formatDateTime(detail.closed_at)}</span></div> : null}
                </div>

                {/* Orders summary */}
                <div className="rounded-xl border border-pd p-4 text-sm">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-2">Orders</div>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div><span className="text-muted-sage">Total </span><span className="font-mono" data-testid="admin-customer-orders-count">{detail.orders_count ?? 0}</span></div>
                    <div><span className="text-muted-sage">Paid </span><span className="font-mono">{detail.orders_paid_count ?? 0}</span></div>
                    <div><span className="text-muted-sage">Lifetime </span><span className="font-mono">{formatMoneyPence(detail.lifetime_spend_pence)}</span></div>
                  </div>
                  {(detail.recent_orders || []).length > 0 ? (
                    <div className="mt-3 space-y-1 text-xs">
                      {detail.recent_orders.map((o) => (
                        <div key={o.order_ref} className="flex justify-between font-mono text-muted-sage">
                          <span>{o.order_ref}</span>
                          <span>{o.quantity} × · {formatMoneyPence(o.amount_total)} · {o.status}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>

                {/* Actions */}
                <div className="rounded-xl border border-pd p-4">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-3">Actions</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      disabled={actionBusy || detail.status !== 'active'}
                      onClick={() => { if (window.confirm('Pause this account for 7 days?')) doAction((id) => adminPauseUser(id, 'admin action'), 'Account paused for 7 days.'); }}
                      className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-pd bg-[color:var(--pd-bg)] text-sm hover:border-[color:var(--pd-accent-yellow)] disabled:opacity-40"
                      data-testid="admin-customer-pause"
                    >
                      <Pause size={14} /> Pause (7d)
                    </button>
                    <button
                      type="button"
                      disabled={actionBusy || detail.status === 'suspended' || detail.status === 'closed'}
                      onClick={() => { if (window.confirm('Formally suspend this account for 6 months? This CANNOT be reversed by the customer.')) doAction((id) => adminSuspendUser(id, 'admin action'), 'Account suspended for 6 months.'); }}
                      className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-[color:var(--pd-danger)]/50 bg-[color:var(--pd-danger)]/10 text-sm text-[color:var(--pd-danger)] hover:border-[color:var(--pd-danger)] disabled:opacity-40"
                      data-testid="admin-customer-suspend"
                    >
                      <Ban size={14} /> Suspend (6mo)
                    </button>
                    <button
                      type="button"
                      disabled={actionBusy || detail.status !== 'paused'}
                      onClick={() => { if (window.confirm('Reactivate this account now?')) doAction((id) => adminReactivateUser(id), 'Account reactivated.'); }}
                      className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-pd bg-[color:var(--pd-bg)] text-sm hover:border-[color:var(--pd-accent-yellow)] disabled:opacity-40"
                      data-testid="admin-customer-reactivate"
                    >
                      <RotateCcw size={14} /> Reactivate
                    </button>
                    <button
                      type="button"
                      disabled={actionBusy || detail.status === 'closed'}
                      onClick={() => { if (window.confirm(`Send a password reset email to ${detail.email}?`)) doAction((id) => adminSendResetToUser(id), 'Reset email sent.'); }}
                      className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-pd bg-[color:var(--pd-bg)] text-sm hover:border-[color:var(--pd-accent-yellow)] disabled:opacity-40"
                      data-testid="admin-customer-send-reset"
                    >
                      <KeyRound size={14} /> Send reset email
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default AdminCustomers;
